import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Spinner, Button, Input, Textarea, Switch, Chip, Select, SelectItem,
} from '@nextui-org/react';
import {
  ArrowLeft, Save, MapPin, Plus, Trash2, Upload, X,
  Phone, Mail, Link2, Calendar, EuroIcon, Hash,
} from 'lucide-react';
import tournamentService from '../services/tournamentService';
import type { Tournament, Address, DartCloudTime } from '../services/tournamentService';

const TEAL = '#3FCED2';

const TOURNAMENT_TYPES = [
  { value: 'single', label: 'Einzel' },
  { value: 'double', label: 'Doppel' },
  { value: 'team', label: 'Team' },
];

const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const timeToString = (t?: DartCloudTime | null): string => {
  if (!t || typeof t.hour !== 'number' || typeof t.minute !== 'number') return '';
  return `${String(t.hour).padStart(2, '0')}:${String(t.minute).padStart(2, '0')}`;
};

const stringToTime = (s: string): DartCloudTime | null => {
  if (!s) return null;
  const [h, m] = s.split(':').map((x) => parseInt(x, 10));
  if (isNaN(h) || isNaN(m)) return null;
  return { hour: h, minute: m };
};

const dateInputFromTs = (ts: number): string => {
  const d = new Date(ts);
  if (isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const EditTournamentPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [changed, setChanged] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [name, setName] = useState('');
  const [type, setType] = useState('single');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [description, setDescription] = useState('');
  const [gameMode, setGameMode] = useState('');
  const [address, setAddress] = useState<Address | null>(null);
  const [categories, setCategories] = useState<string[]>([]);

  // Dates
  const [dates, setDates] = useState<string[]>([]);
  const [newDate, setNewDate] = useState('');

  // Times
  const [doorsOpen, setDoorsOpen] = useState('');
  const [start, setStart] = useState('');
  const [entryTime, setEntryTime] = useState('');

  // Entry
  const [entryEnabled, setEntryEnabled] = useState(false);
  const [entryFee, setEntryFee] = useState<string>('');
  const [entryLimit, setEntryLimit] = useState<string>('');
  const [entryDeadline, setEntryDeadline] = useState<string>('');
  const [entryCode, setEntryCode] = useState('');
  const [entryLink, setEntryLink] = useState('');
  const [entryText, setEntryText] = useState('');

  // Visibility
  const [visible, setVisible] = useState(true);
  const [cancelled, setCancelled] = useState(false);

  const fetchTournament = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await tournamentService.getTournamentById(id);
      const t = (res as any)?.data ?? res;
      if (!t) { setMessage({ type: 'error', text: 'Turnier konnte nicht geladen werden' }); return; }
      setTournament(t);
      setName(t.name ?? '');
      setType(t.type ?? 'single');
      setEmail(t.email ?? '');
      setPhone(t.phone ?? '');
      setDescription(t.description ?? '');
      setGameMode(t.gameMode ?? '');
      setAddress(t.address ?? null);
      setCategories(t.categories ?? []);
      setDates(Array.isArray(t.dates) ? t.dates.map((ts: number) => dateInputFromTs(ts)).filter((s: string) => s !== '') : []);
      setDoorsOpen(timeToString(t.doorsOpen));
      setStart(timeToString(t.start));
      setEntryTime(timeToString(t.entryTime));
      setEntryEnabled(t.entryEnabled ?? false);
      setEntryFee(t.entryFee != null ? String(t.entryFee) : '');
      setEntryLimit(t.entryLimit != null ? String(t.entryLimit) : '');
      setEntryDeadline(t.entryDeadline ? dateInputFromTs(t.entryDeadline) : '');
      setEntryCode(t.entryCode ?? '');
      setEntryLink(t.entryLink ?? '');
      setEntryText(t.entryText ?? '');
      setVisible(t.visible ?? true);
      setCancelled(t.cancelled ?? false);
      setChanged(false);
    } catch (err) {
      console.error(err);
      setMessage({ type: 'error', text: 'Turnier konnte nicht geladen werden' });
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchTournament(); window.scrollTo({ top: 0 }); }, [fetchTournament]);
  const markChanged = () => setChanged(true);

  const toggleCategory = (cat: string) => {
    setCategories((prev) => prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat]);
    markChanged();
  };

  const addDate = () => {
    if (!newDate || dates.includes(newDate)) return;
    setDates([...dates, newDate].sort());
    setNewDate('');
    markChanged();
  };

  const removeDate = (d: string) => {
    setDates(dates.filter((x) => x !== d));
    markChanged();
  };

  const handleSave = async () => {
    if (!name.trim()) { setMessage({ type: 'error', text: 'Bitte einen Namen festlegen' }); return; }
    if (!tournament?._id && !tournament?.id) return;
    setSaving(true); setMessage(null);
    try {
      const dateTimestamps = dates.map((d) => new Date(d).getTime()).filter((t) => !isNaN(t));
      const payload: any = {
        id: tournament._id ?? tournament.id,
        name,
        type,
        email: email.trim() === '' ? null : email,
        phone: phone.trim() === '' ? null : phone,
        description: description.trim() === '' ? null : description,
        gameMode: gameMode.trim() === '' ? null : gameMode,
        categories,
        dates: dateTimestamps,
        doorsOpen: stringToTime(doorsOpen),
        start: stringToTime(start),
        entryTime: stringToTime(entryTime),
        entryEnabled,
        entryFee: entryFee === '' ? null : parseFloat(entryFee),
        entryLimit: entryLimit === '' ? null : parseInt(entryLimit, 10),
        entryDeadline: entryDeadline === '' ? null : new Date(entryDeadline).getTime(),
        entryCode: entryCode.trim() === '' ? null : entryCode,
        entryLink: entryLink.trim() === '' ? null : entryLink,
        entryText: entryText.trim() === '' ? null : entryText,
        visible,
        cancelled,
      };
      await tournamentService.updateTournament(payload);
      setChanged(false);
      setMessage({ type: 'success', text: 'Erfolgreich aktualisiert' });
      await fetchTournament();
    } catch (err: any) {
      console.error(err);
      setMessage({ type: 'error', text: err?.response?.data?.message || 'Aktualisierung fehlgeschlagen' });
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    if (changed && !window.confirm('Deine Änderungen wurden noch nicht gespeichert. Wirklich abbrechen?')) return;
    navigate('/verwaltung?tab=tournaments');
  };

  const handlePosterUpload = async (file: File) => {
    if (!tournament?._id && !tournament?.id) return;
    if (file.size > 20 * 1024 * 1024) { setMessage({ type: 'error', text: 'Datei zu groß (Max. 20 MB)' }); return; }
    setSaving(true);
    try {
      const base64 = await fileToBase64(file);
      await tournamentService.updateTournament({ id: tournament._id ?? tournament.id, image: base64 });
      await fetchTournament();
      setMessage({ type: 'success', text: 'Plakat aktualisiert' });
    } catch { setMessage({ type: 'error', text: 'Upload fehlgeschlagen' }); }
    finally { setSaving(false); }
  };

  const handlePosterRemove = async () => {
    if (!tournament?._id && !tournament?.id) return;
    if (!window.confirm('Plakat wirklich entfernen?')) return;
    setSaving(true);
    try {
      await tournamentService.updateTournament({ id: tournament._id ?? tournament.id, image: null });
      await fetchTournament();
      setMessage({ type: 'success', text: 'Plakat entfernt' });
    } catch { setMessage({ type: 'error', text: 'Entfernen fehlgeschlagen' }); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!tournament?._id && !tournament?.id) return;
    if (!window.confirm('Turnier wirklich unwiderruflich löschen?')) return;
    setSaving(true);
    try {
      await tournamentService.deleteTournament((tournament._id ?? tournament.id) as string);
      navigate('/verwaltung?tab=tournaments');
    } catch (err: any) {
      console.error(err);
      setMessage({ type: 'error', text: err?.response?.data?.message || 'Löschen fehlgeschlagen' });
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-black text-white flex items-center justify-center">
      <Spinner color="primary" size="lg" label="Turnier laden..." /></div>;
  }
  if (!tournament) {
    return <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center px-4">
      <p className="text-zinc-400 mb-4">Turnier konnte nicht geladen werden.</p>
      <Button onPress={handleBack} startContent={<ArrowLeft size={16} />}>Zurück</Button>
    </div>;
  }

  return (
    <div className="min-h-screen bg-black text-white pb-32">
      <Helmet><title>{name || 'Turnier'} bearbeiten - DartCloud</title></Helmet>

      <div className="sticky top-0 z-10 bg-black/80 backdrop-blur border-b border-zinc-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
          <button onClick={handleBack} className="text-zinc-300 hover:text-white p-1"><ArrowLeft size={20} /></button>
          <h1 className="text-lg font-bold flex-1 truncate">Turnier bearbeiten</h1>
          {changed && <Chip size="sm" color="warning" variant="flat">Ungespeichert</Chip>}
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-8">
        {message && (
          <div className={`rounded-xl p-3 text-sm flex items-start gap-2 ${
            message.type === 'success' ? 'bg-green-500/10 border border-green-500/30 text-green-300' : 'bg-red-500/10 border border-red-500/30 text-red-300'
          }`}>
            <span className="flex-1">{message.text}</span>
            <button onClick={() => setMessage(null)}><X size={14} /></button>
          </div>
        )}

        <section>
          <h2 className="text-base font-semibold mb-2">Name</h2>
          <Input value={name} onValueChange={(v) => { setName(v); markChanged(); }} placeholder="Name"
            classNames={{ inputWrapper: 'bg-zinc-900 border border-zinc-800' }} />
        </section>

        <section>
          <h2 className="text-base font-semibold mb-2">Turniertyp</h2>
          <Select selectedKeys={[type]}
            onSelectionChange={(keys) => {
              const v = Array.from(keys)[0] as string;
              if (v) { setType(v); markChanged(); }
            }}
            classNames={{ trigger: 'bg-zinc-900 border border-zinc-800' }}
          >
            {TOURNAMENT_TYPES.map((t) => <SelectItem key={t.value}>{t.label}</SelectItem>)}
          </Select>
        </section>

        <section>
          <h2 className="text-base font-semibold mb-2">Kategorien</h2>
          <div className="flex flex-wrap gap-2">
            {(['steeldart', 'edart', 'online'] as const).map((cat) => {
              const active = categories.includes(cat);
              return (
                <button key={cat} onClick={() => toggleCategory(cat)}
                  className={`px-3 py-1.5 rounded-full text-sm border transition ${
                    active ? 'border-transparent' : 'border-zinc-700 bg-zinc-900 text-zinc-400 hover:text-white'
                  }`}
                  style={active ? { backgroundColor: TEAL, color: '#000' } : {}}
                >
                  {cat === 'steeldart' ? 'Steeldart' : cat === 'edart' ? 'E-Dart' : 'Online'}
                </button>
              );
            })}
          </div>
        </section>

        <section>
          <h2 className="text-base font-semibold mb-2 flex items-center gap-2">
            <Calendar size={16} /> Termine
          </h2>
          <div className="flex gap-2 mb-3">
            <Input type="date" value={newDate} onValueChange={setNewDate}
              classNames={{ inputWrapper: 'bg-zinc-900 border border-zinc-800' }} />
            <Button onPress={addDate} isDisabled={!newDate}
              style={{ backgroundColor: TEAL, color: '#000' }} startContent={<Plus size={14} />}>
              Hinzufügen
            </Button>
          </div>
          {dates.length === 0 ? (
            <p className="text-sm text-zinc-400">Noch keine Termine festgelegt.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {dates.map((d) => (
                <div key={d} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border border-zinc-700 bg-zinc-900">
                  <span>{new Date(d).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' })}</span>
                  <button onClick={() => removeDate(d)} className="text-zinc-400 hover:text-red-400">
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>

        <section>
          <h2 className="text-base font-semibold mb-2">Spielmodus</h2>
          <Input value={gameMode} onValueChange={(v) => { setGameMode(v); markChanged(); }}
            placeholder="z.B. 501 Double Out, Best of 5 Legs"
            startContent={<Hash size={14} className="text-zinc-400" />}
            classNames={{ inputWrapper: 'bg-zinc-900 border border-zinc-800' }} />
        </section>

        <section>
          <h2 className="text-base font-semibold mb-2">Uhrzeiten</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Input value={doorsOpen} onValueChange={(v) => { setDoorsOpen(v); markChanged(); }}
              type="time" label="Einlass"
              classNames={{ inputWrapper: 'bg-zinc-900 border border-zinc-800' }} />
            <Input value={start} onValueChange={(v) => { setStart(v); markChanged(); }}
              type="time" label="Start"
              classNames={{ inputWrapper: 'bg-zinc-900 border border-zinc-800' }} />
            <Input value={entryTime} onValueChange={(v) => { setEntryTime(v); markChanged(); }}
              type="time" label="Anmeldeschluss vor Ort"
              classNames={{ inputWrapper: 'bg-zinc-900 border border-zinc-800' }} />
          </div>
        </section>

        <section>
          <h2 className="text-base font-semibold mb-2">Anmeldung</h2>
          <div className="space-y-3">
            <div className="flex items-center justify-between bg-zinc-900/50 border border-zinc-800 rounded-xl p-3">
              <span className="text-sm">Anmeldung aktiv</span>
              <Switch isSelected={entryEnabled} onValueChange={(v) => { setEntryEnabled(v); markChanged(); }} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input value={entryFee} onValueChange={(v) => { setEntryFee(v); markChanged(); }}
                type="number" step="0.01" label="Startgebühr (€)"
                startContent={<EuroIcon size={14} className="text-zinc-400" />}
                classNames={{ inputWrapper: 'bg-zinc-900 border border-zinc-800' }} />
              <Input value={entryLimit} onValueChange={(v) => { setEntryLimit(v); markChanged(); }}
                type="number" label="Anmeldelimit"
                classNames={{ inputWrapper: 'bg-zinc-900 border border-zinc-800' }} />
            </div>
            <Input value={entryDeadline} onValueChange={(v) => { setEntryDeadline(v); markChanged(); }}
              type="date" label="Anmeldeschluss (Datum)"
              classNames={{ inputWrapper: 'bg-zinc-900 border border-zinc-800' }} />
            <Input value={entryCode} onValueChange={(v) => { setEntryCode(v); markChanged(); }}
              label="Anmeldecode" placeholder="Optional"
              classNames={{ inputWrapper: 'bg-zinc-900 border border-zinc-800' }} />
            <Input value={entryLink} onValueChange={(v) => { setEntryLink(v); markChanged(); }}
              label="Externer Anmeldelink" placeholder="https://..."
              startContent={<Link2 size={14} className="text-zinc-400" />}
              classNames={{ inputWrapper: 'bg-zinc-900 border border-zinc-800' }} />
            <Textarea value={entryText} onValueChange={(v) => { setEntryText(v); markChanged(); }}
              label="Hinweis zur Anmeldung" minRows={2}
              classNames={{ inputWrapper: 'bg-zinc-900 border border-zinc-800' }} />
          </div>
        </section>

        <section>
          <h2 className="text-base font-semibold mb-2">Kontakt</h2>
          <div className="space-y-3">
            <Input value={email} onValueChange={(v) => { setEmail(v); markChanged(); }} placeholder="E-Mail" type="email"
              startContent={<Mail size={16} className="text-zinc-400" />}
              classNames={{ inputWrapper: 'bg-zinc-900 border border-zinc-800' }} />
            <Input value={phone} onValueChange={(v) => { setPhone(v); markChanged(); }} placeholder="Telefon"
              startContent={<Phone size={16} className="text-zinc-400" />}
              classNames={{ inputWrapper: 'bg-zinc-900 border border-zinc-800' }} />
          </div>
        </section>

        <section>
          <h2 className="text-base font-semibold mb-2">Adresse</h2>
          {address ? (
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-3 flex items-center gap-3">
              <MapPin size={18} className="text-zinc-400 flex-shrink-0" />
              <span className="flex-1 text-sm truncate">{address.label || tournamentService.formatAddress(address)}</span>
              <Button isIconOnly size="sm" variant="light" color="danger"
                onPress={() => { setAddress(null); markChanged(); }}><Trash2 size={14} /></Button>
            </div>
          ) : (
            <p className="text-sm text-zinc-400">Keine Adresse hinterlegt. Adressen können aktuell nur in der Mobile-App geändert werden.</p>
          )}
        </section>

        <section>
          <h2 className="text-base font-semibold mb-2">Beschreibung</h2>
          <Textarea value={description} onValueChange={(v) => { setDescription(v); markChanged(); }}
            placeholder="Beschreibung des Turniers" minRows={6}
            classNames={{ inputWrapper: 'bg-zinc-900 border border-zinc-800' }} />
        </section>

        <section>
          <h2 className="text-base font-semibold mb-1">Plakat</h2>
          {tournament.image && (
            <div className="mb-3">
              <div className="h-48 bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden">
                <img src={tournamentService.getImageUrl(tournament) ?? ''} alt="Plakat" className="w-full h-full object-contain" />
              </div>
              <Button size="sm" variant="light" color="danger" className="mt-2" onPress={handlePosterRemove}
                startContent={<Trash2 size={14} />}>Plakat entfernen</Button>
            </div>
          )}
          <label className="inline-flex items-center gap-2 px-4 py-2 bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 rounded-xl cursor-pointer text-sm">
            <Upload size={14} /> Plakat hochladen
            <input type="file" accept="image/*" className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handlePosterUpload(f); e.target.value = ''; }} />
          </label>
        </section>

        <section>
          <h2 className="text-base font-semibold mb-2">Sichtbarkeit</h2>
          <div className="space-y-3">
            <div className="flex items-center justify-between bg-zinc-900/50 border border-zinc-800 rounded-xl p-3">
              <span className="text-sm">Öffentlich sichtbar</span>
              <Switch isSelected={visible} onValueChange={(v) => { setVisible(v); markChanged(); }} />
            </div>
            <div className="flex items-center justify-between bg-zinc-900/50 border border-zinc-800 rounded-xl p-3">
              <span className="text-sm">Turnier abgesagt</span>
              <Switch isSelected={cancelled} onValueChange={(v) => { setCancelled(v); markChanged(); }} />
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-base font-semibold mb-2 text-red-400">Gefahrenzone</h2>
          <Button color="danger" variant="bordered" startContent={<Trash2 size={16} />} onPress={handleDelete}>
            Turnier löschen
          </Button>
        </section>
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-black/95 backdrop-blur border-t border-zinc-800 z-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3">
          <Button className="w-full" isDisabled={!changed || saving} isLoading={saving} onPress={handleSave}
            startContent={!saving ? <Save size={16} /> : null}
            style={{ backgroundColor: TEAL, color: '#000' }}>Aktualisieren</Button>
        </div>
      </div>
    </div>
  );
};

export default EditTournamentPage;
